import { DataProvider } from "@refinedev/core";
import dataProvider, { stringify } from "@refinedev/simple-rest";
import {
  axiosInstance,
  generateSort,
  generateFilter,
  toHttpError,
} from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

// Resources where we cache the COUNT result to skip re-counting on page navigation
const HINT_TOTAL_RESOURCES = new Set(["quotes", "orders", "contacts"]);
const HINT_TTL_MS = 60_000; // 1 minute

interface TotalCacheEntry { total: number; expiresAt: number }
const totalCache = new Map<string, TotalCacheEntry>();

function makeTotalCacheKey(resource: string, filters: any, sorters: any): string {
  return `${resource}::${JSON.stringify(filters ?? [])}::${JSON.stringify(sorters ?? [])}`;
}

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const configRequest = (meta?: any, defaultMethod: string = "get") => {
  const { headers: customHeaders, method } = meta ?? {};
  return {
    headers: {
      ...customHeaders,
    },
    method: method ?? defaultMethod,
  };
};

const buildQueryParams = (pagination: any, sorters: any, filters: any) => {
  const { current = 1, pageSize = 20, mode = "server" } = pagination ?? {};
  const query: Record<string, any> = {};

  if (mode === "server") {
    query.page = current;
    query.limit = pageSize;
  }

  const generatedSort = generateSort(sorters);
  if (generatedSort) {
    query._sort = generatedSort.sortBy;
    query._order = generatedSort.sortBy;
  }

  const generatedFilter = generateFilter(filters);
  return { ...query, ...generatedFilter };
};

export const xconsDataProvider: DataProvider = {
  ...dataProvider(API_BASE_URL),

  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    const { current = 1, pageSize = 20 } = pagination ?? {};
    const query: Record<string, any> = {
      page: current,
      limit: pageSize,
      ...generateSort(sorters),
      ...generateFilter(filters),
    };

    // Inject cached total so backend skips COUNT on page navigation
    const cacheKey = HINT_TOTAL_RESOURCES.has(resource)
      ? makeTotalCacheKey(resource, filters, sorters)
      : null;

    if (cacheKey) {
      const entry = totalCache.get(cacheKey);
      if (entry && Date.now() < entry.expiresAt) {
        query["filter._hint_total"] = `$eq:${entry.total}`;
      }
    }

    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/${resource}`, {
        params: query,
      });

      const havePagination = data?.meta.pagination
        ? data?.meta.pagination
        : data.meta;

      const total = havePagination?.totalItems;
      const adjustedPageSize = Math.min(pageSize, total);

      // Cache the total for subsequent page navigations (same filters/sorters)
      if (cacheKey && total > 0) {
        totalCache.set(cacheKey, { total, expiresAt: Date.now() + HINT_TTL_MS });
      }

      return {
        data: data?.data,
        total,
        meta: {
          pagination: {
            current,
            pageSize: adjustedPageSize,
            total,
          },
          // Productos asociados: cuántos no tienen todavía el % de match calculado
          // (xcons-docs#624). Los demás listados no lo mandan.
          ...(data?.meta?.matchPending !== undefined && {
            matchPending: data.meta.matchPending,
          }),
        },
      };
    } catch (error) {
      console.error("Error in getList:", error);
      throw toHttpError(error);
    }
  },

  getOne: async ({ resource, id, meta }) => {
    const url = `${API_BASE_URL}/${resource}/${id}`;
    const config = configRequest(meta);

    try {
      const { data } = await axiosInstance.get(url, config);
      return { data };
    } catch (error) {
      console.error("Error in getOne:", error);
      throw toHttpError(error);
    }
  },

  getMany: async ({ resource, ids, meta }) => {
    const url = `${API_BASE_URL}/${resource}?${stringify({ id: ids })}`;
    const config = configRequest(meta);

    try {
      const { data } = await axiosInstance.get(url, config);
      return { data };
    } catch (error) {
      console.error("Error in getMany:", error);
      throw toHttpError(error);
    }
  },

  create: async ({ resource, variables, meta }) => {
    const url = `${API_BASE_URL}/${resource}`;
    const config = configRequest(meta, "post");

    try {
      const { data } = await axiosInstance.post(url, variables, config);
      return { data };
    } catch (error) {
      console.error("Error in create:", error);
      throw toHttpError(error);
    }
  },

  update: async ({ resource, id, variables, meta }) => {
    const url = `${API_BASE_URL}/${resource}/${id}`;
    const config = configRequest(meta, "patch");

    try {
      const { data } = await axiosInstance.patch(url, variables, config);
      return { data };
    } catch (error) {
      console.error("Error in update:", error);
      throw toHttpError(error);
    }
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const url = `${API_BASE_URL}/${resource}/${id}`;
    const config = configRequest(meta, "delete");

    try {
      const { data } = await axiosInstance.delete(url, {
        data: variables,
        ...config,
      });
      return { data };
    } catch (error) {
      console.error("Error in deleteOne:", error);
      throw toHttpError(error);
    }
  },

  getApiUrl: () => API_BASE_URL,
};
